import {
  Activity,
  AlertTriangle,
  Box,
  CheckCircle,
  CheckSquare,
  CircleSlash,
  Clock,
  Cpu,
  Info,
  Lock,
  LogIn,
  LogOut,
  Mail,
  Monitor,
  Rocket,
  Search,
  Shield,
  ShieldOff,
  Target,
  User,
  UserCheck,
  Users,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import type { Severity } from '~/features/notifications/types/notifications.api.types';
import { cn } from '~/utils/cn';

/**
 * Backend card `icon` (kebab-case lucide names) → lucide-react component. Keeps
 * the notification glyph server-driven; a new event type only needs a row here.
 * Unknown/absent names fall back to the severity-derived default glyph.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  user: User,
  mail: Mail,
  'x-circle': XCircle,
  'log-out': LogOut,
  'user-check': UserCheck,
  'slash-circle': CircleSlash,
  users: Users,
  'shield-off': ShieldOff,
  'check-circle': CheckCircle,
  target: Target,
  activity: Activity,
  rocket: Rocket,
  'alert-triangle': AlertTriangle,
  lock: Lock,
  monitor: Monitor,
  shield: Shield,
  clock: Clock,
  'log-in': LogIn,
  cpu: Cpu,
};

interface Props {
  severity: Severity;
  type: string;
  /** Backend-supplied lucide icon name (kebab-case). When it maps to a known
   *  icon it replaces the severity default glyph; the tint stays severity-driven. */
  icon?: string | undefined;
  /**
   * When true, wraps the icon in a 9×9 colored rounded-lg container (full-page cards).
   * When false (default), renders the bare icon for use inside an existing container.
   */
  withContainer?: boolean;
}

/** Icon representing a notification — the backend `icon` glyph when provided,
 *  otherwise a severity/type default — with a severity-tinted optional container. */
export function NotificationSeverityIcon({ severity, type, icon, withContainer = false }: Props) {
  const iconClass = 'h-4 w-4 shrink-0';

  // Glyph colour is always the brand primary; only the container tint stays
  // severity/type driven. A backend `icon` only swaps which glyph is drawn.
  let DefaultIcon: LucideIcon;
  let containerClass: string;

  if (type.startsWith('calibration.') || type.startsWith('catalog.')) {
    DefaultIcon = type.startsWith('catalog.') ? Box : Search;
    containerClass = 'bg-blue-50';
  } else if (severity === 'critical' || severity === 'high') {
    DefaultIcon = AlertTriangle;
    containerClass = 'bg-red-50';
  } else if (severity === 'medium') {
    DefaultIcon = CheckSquare;
    containerClass = 'bg-blue-50';
  } else {
    DefaultIcon = Info;
    containerClass = 'bg-gray-100';
  }

  const Glyph = (icon ? ICON_MAP[icon] : undefined) ?? DefaultIcon;
  const glyph = <Glyph className={cn(iconClass, 'text-primary')} />;

  if (!withContainer) return glyph;

  return (
    <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', containerClass)}>
      {glyph}
    </div>
  );
}
